import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RoleBasedParamModel } from '../../../core/models/role-based-param.model';
import { ThenableEventEmitter } from '../../utils/thenable-event';
import { CustomFilter } from './models/custom-filter.model';
import { FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { AutoCompleteRefModel } from '../../../core/models/autocomplete-ref.model';
import { SelectAutocompleteService } from '../../../core/services/select-autocomplete.service';
import { SelectAutocompleteComponent } from '../mat-select-autocomplete/select-autocomplete.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CustomFilterButton } from './models/custom-filter-button.model';
import { UtilsService } from '../../../core/services/utils.service';
import { AutocompleteModel } from '../../../core/models/autocomplete.model';
import { FilterType } from './models/filter-type.model';

@Component({
    selector: 'ag-custom-filters',
    templateUrl: './custom-filters.component.html',
    styleUrls: ['./custom-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomFiltersComponent implements OnInit, AfterViewInit {
  FilterType = FilterType;
  subscription = new Subscription();
  @ViewChildren(SelectAutocompleteComponent) autocompleteDropdowns: SelectAutocompleteComponent[];
  @ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;
  
  /** Input used to provide an array of configurable filters to render the component */
  @Input()
  public filters: CustomFilter[] = [];
  /** Input used to provide group validators to be applied too the form */
  @Input()
  public groupValidators: ValidatorFn[] = [];
  /** Input used to provide the parent component */
  @Input()
  public instance: Component;
  /** Input used to activate the export feature */
  @Input()
  public hasExport: boolean = false;
  /** Input used to customise the search button label */
  @Input()
  public searchLabel: string = 'Search';
  /** Input used to customise the search button size */
  @Input()
  public searchFlexSize: string = '15%';
  /** Input used to provide custom buttons */
  @Input()
  public customButtons: CustomFilterButton[] = [];
  /** Input used to instantiate the component with a closed expansion panel */
  @Input()
  public isExpansionOpen = true;
  /** Input used to disable the whole filter section */
  @Input()
  public notEditable = false;
  /** Input used to customise the filter section name */
  @Input()
  public filterSectionTitle = 'Filters';
  @Input()
  public disableSearchButton = false;
  @Input()
  public permanentResidenceCountryKey = false;

  /** Event emitted on search button press if the form is valid */
  @Output()
  public search = new ThenableEventEmitter<any>();
  /** Event emitted on export button press if the form is valid */
  @Output()
  public export = new ThenableEventEmitter<any>();
  /** Event emitted if the form is invalid */
  @Output()
  public invalidForm = new ThenableEventEmitter<any>();
  /** Event emitted on any filter change */
  @Output()
  public filterChange = new ThenableEventEmitter<any>();
  /** Event emitted on any filter clear */
  @Output()
  public filterClear = new ThenableEventEmitter<any>();
  @Output()
  public formClear = new ThenableEventEmitter<any>();

  file: File;
  form: FormGroup;
  public refModels: Map<string, AutoCompleteRefModel> = new Map();

  public disableClear = false;
  /** Form values once the defaults are set; Clear is only enabled when the user changed something since. */
  private baselineValue: string;

  constructor(
    private cdref: ChangeDetectorRef,
    private fb: FormBuilder,
    private autocompleteService: SelectAutocompleteService,
  ) {
    this.form = this.fb.group({});
  }

  /** Function used to set values that will remain on the filters no matter what ( even on clear ) */
  setDefaultValues() {
    this.filters.forEach(filter => {
      if (filter.type === FilterType.DATE) {
        if (filter.datePickerRange) {
          if (filter.defaultValues?.length == 1) {
            this.form.controls[filter.id + 'Start'].setValue(filter.defaultValues[0]);
          } else if (filter.defaultValues?.length == 2) {
            this.form.controls[filter.id + 'Start'].setValue(filter.defaultValues[0]);
            this.form.controls[filter.id + 'End'].setValue(filter.defaultValues[1]);
          }
        } else {
          if (filter.defaultValues?.length == 1) {
            this.form.controls[filter.id].setValue(filter.defaultValues[0]);
          }
        }
      } else if ([FilterType.SELECT, FilterType.AUTOCOMPLETE, FilterType.AUTOCOMPLETE_EAGER].includes(filter.type)) {
        // Tested for autocomplete:
        if (filter.multipleSelect) {
          this.form.controls[filter.id].setValue(filter.defaultValues);
        } else {
          this.form.controls[filter.id].setValue(filter.defaultValues[0]);
        }
      } else {
        if (filter.defaultValues?.length == 1) {
          this.form.controls[filter.id].setValue(filter.defaultValues[0]);
        }
      }
    })
  }

  /** Function used to set values to instantiate the filters with, but can be clearable */
  setPreservedValues() {
    this.filters.forEach(filter => {
      if (filter.type === FilterType.DATE) {
        if (filter.datePickerRange) {
          if (filter.preservedValues?.length == 1) {
            this.form.controls[filter.id + 'Start'].setValue(filter.preservedValues[0]);
          } else if (filter.preservedValues?.length == 2) {
            this.form.controls[filter.id + 'Start'].setValue(filter.preservedValues[0]);
            this.form.controls[filter.id + 'End'].setValue(filter.preservedValues[1]);
          }
        } else {
          if (filter.preservedValues?.length == 1) {
            this.form.controls[filter.id].setValue(filter.preservedValues[0]);
          }
        }
      } else if ([FilterType.SELECT, FilterType.AUTOCOMPLETE, FilterType.AUTOCOMPLETE_EAGER].includes(filter.type) && filter.preservedValues) {
        this.form.controls[filter.id].setValue(filter.preservedValues);
      } else {
        if (filter.preservedValues?.length == 1) {
          this.form.controls[filter.id].setValue(filter.preservedValues[0]);
        }
      }
    })
  }

  ngOnInit(): void {
    this.filters.forEach(filter => {
      if (filter.type === FilterType.DATE) {
        if (filter.datePickerRange) {
          this.form.setControl(filter.id + 'Start', new FormControl<string>(null));
          this.form.setControl(filter.id + 'End', new FormControl<string>(null));
          this.form.controls[filter.id + 'Start'].valueChanges.subscribe(value => this.emitTextInputChange(value, filter.id + 'Start'));
          this.form.controls[filter.id + 'End'].valueChanges.subscribe(value => this.emitTextInputChange(value, filter.id + 'End'));
          if (this.notEditable || filter.disabled) {
            this.form.controls[filter.id + 'Start'].disable();
            this.form.controls[filter.id + 'End'].disable();
          }
        } else {
          this.form.setControl(filter.id, new FormControl<string>(null));
          this.form.controls[filter.id].valueChanges.subscribe(value => this.emitTextInputChange(value, filter.id));
          if (this.notEditable || filter.disabled) {
            this.form.controls[filter.id].disable();
          }
        }
      } else if ([FilterType.SELECT, FilterType.AUTOCOMPLETE, FilterType.AUTOCOMPLETE_EAGER].includes(filter.type)) {
        if (filter.multipleSelect) {
          this.form.setControl(filter.id, new FormControl<string[]>(null));
        } else {
          this.form.setControl(filter.id, new FormControl<string>(null));
        }
        if (this.notEditable || filter.disabled) {
          this.form.controls[filter.id].disable();
        }
        if (filter.type === FilterType.AUTOCOMPLETE) {
          this.refModels.set(filter.id, new AutoCompleteRefModel(
            this.form.controls[filter.id],
            (value: string | any) => filter.fetchCallback(value, this.instance)
          ));
          this.autocompleteService.addAutocompleteEvent(this.refModels.get(filter.id));
        }
        if ((filter.type === FilterType.SELECT || filter.type === FilterType.AUTOCOMPLETE_EAGER)
          && filter.validators?.length > 0) {
          this.form.controls[filter.id].setValidators(filter.validators);
        }
      } else {
        this.form.setControl(filter.id, new FormControl<string>(null));
        this.form.controls[filter.id].valueChanges.subscribe(value => this.emitTextInputChange(value, filter.id));
        this.form.controls[filter.id].setValidators(filter.validators);
        if (this.notEditable || filter.disabled) {
          this.form.controls[filter.id].disable();
        }
      }
    });

    this.setDefaultValues();
    this.setPreservedValues();

    if (this.permanentResidenceCountryKey) {
      this.form.markAsDirty();
      this.disableClear = true;
    }
  }

  ngAfterViewInit(): void {
    if (this.groupValidators?.length) {
      this.form.addValidators(this.groupValidators);
      this.form.reset();
    }
    this.setDefaultValues();
    this.setPreservedValues();
    this.captureBaseline();
    
    if (!this.isExpansionOpen) {
      this.matExpansionPanel.close();
    }
    this.cdref.detectChanges();
  }

  private getSearchCriteria(): any {
    const searchCriteria: any = {};

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.controls[key] as FormControl;
      searchCriteria[key] = control.value;

      let filter;
      if (key.includes('Start') || key.includes('End')) {
        filter = this.filters.find(filter => filter.id === key.replace('Start', '').replace('End', ''));
      } else {
        filter = this.filters.find(filter => filter.id === key);
        if (filter.type !== 'date') {
          filter = undefined;
        }
      }
      if (filter) {
        if (searchCriteria[key]) {
          if (key.includes('End')) {
            searchCriteria[key] = UtilsService.dateToServerDate(UtilsService.endOfTheDay(searchCriteria[key]));
          } else {
            searchCriteria[key] = UtilsService.dateToServerDate(UtilsService.startOfTheDay(searchCriteria[key]));
          }
        }
      }
    });

    return searchCriteria;
  }

  /** True when a field value differs from the values the form was loaded (or last cleared) with. */
  get hasChanges(): boolean {
    return this.baselineValue !== undefined && this.serializeFormValue() !== this.baselineValue;
  }

  /**
   * Snapshot taken on the next tick, after dropdowns (select-autocomplete) have written their initial values back,
   * since those writes go through the value accessor and would otherwise count as user changes.
   */
  private captureBaseline() {
    setTimeout(() => this.baselineValue = this.serializeFormValue());
  }

  private serializeFormValue(): string {
    const value = this.form.getRawValue();
    return JSON.stringify(Object.keys(value).sort().map(key => [key, value[key] ?? '']));
  }

  clear() {
    this.autocompleteDropdowns.forEach((autocomplete: SelectAutocompleteComponent) => autocomplete.clearSearch());
    this.form.reset();
    this.setDefaultValues();

    this.filters.forEach((filter) => filter.preservedValues = undefined);

    this.formClear.emit(this.form);
    this.captureBaseline();

    if (this.permanentResidenceCountryKey) {
      this.form.markAsDirty();
      this.disableClear = true;
    }
  }

  onSearch() {
    if (this.form.valid) {
      if (this.file) {
        this.search.emit({
          formData: this.getSearchCriteria(),
          file: this.file
        }).then(() => this.matExpansionPanel.close());
      } else {
        this.search.emit(this.getSearchCriteria()).then(() => this.matExpansionPanel.close());
      }
    } else {
      this.invalidForm.emit(this.form);
    }
  }

  onExport() {
    if (this.form.valid || this.notEditable) {
      this.export.emit(this.getSearchCriteria()).then(() => this.matExpansionPanel.close());
    } else {
      this.invalidForm.emit(this.form);
    }
  }

  doCustomAction(button: CustomFilterButton) {
    button.action(this.getSearchCriteria(), this.instance);
  }

  emitSelectionChange(event: any, filterId: any) {
    this.filterChange.emit({ event, filterId });
  }

  emitChange(event: any, filterId: any) {    
    if (event?.length > 0 && this.filters?.find(filter => filter.id === filterId).type === FilterType.AUTOCOMPLETE_EAGER) {
      if (!this.form.dirty) this.form.markAsDirty();
    }

    this.filterChange.emit({ event, filterId }).then((filters: any) => {
      filters.forEach((filter: any) => {
        this.autocompleteService.resetAutocomplete(this.refModels.get(filter));
      })
    });
  }

  emitTextInputChange(value: any, filterId: any) {
    if (this.permanentResidenceCountryKey && value) {
      this.disableClear = false;
    }
    this.filterChange.emit({ value: this.form.controls[filterId].value, filterId });
  }

  handleFileInputChange(l: FileList, filter: string) {
    this.file = l[0];
    if (this.file) {
      this.form.controls[filter].patchValue(`${this.file.name}`);
    } else {
      this.form.controls[filter].patchValue('');
    }
  }

  emitClear(filterId: any) {
    this.filterClear.emit({ filterId }).then((filters: any) => {
      filters.forEach((filter: any) => {
        this.autocompleteService.resetAutocomplete(this.refModels.get(filter));
      })
    })
  }

  buildGuardParamBasedRole(): RoleBasedParamModel {
    // Add custom AD role
    let customRoles: string[] = [];
    return {
      customRole: customRoles,
      path: '',
    } as RoleBasedParamModel;
  }

  updateSelectValues(filterId: string, values: AutocompleteModel[]) {
    this.filters.find(filter => filter.id === filterId).values = [];
    this.filters.find(filter => filter.id === filterId).values = values;
    if (this.form.controls[filterId].disabled) {
      this.form.controls[filterId].enable();
    }
  }

  updateInputValues(filterId: string, value: string) {
    this.form.controls[filterId].setValue(value);
  }

  changeDisabledState(filterId: string, state: boolean) {
    if (state) {
      this.form.controls[filterId].enable();
    } else {
      this.form.controls[filterId].disable();
    }
  }

  displayStreetFormatHint(filter: string) {
    return filter === 'houseNumberAndStreet' && !this.filters.find(f => f.id === 'houseNumber');
  }
}
